import React, { useState, useEffect } from 'react';
import { TrendingUp, FileText, Bot, CheckCircle, AlertTriangle, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SubmissionAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(`${API}/api/submissions/analytics/summary`);
        if (res.ok) setData(await res.json());
      } catch (err) { console.error('Failed to load submission analytics:', err); }
      finally { setLoading(false); }
    };
    fetchAnalytics();
  }, []);

  if (loading || !data) return null;

  const decisionTotal = data.decisions.request_materials + data.decisions.pass;
  const reqPct = decisionTotal > 0 ? Math.round((data.decisions.request_materials / decisionTotal) * 100) : 0;

  return (
    <div className="bg-swp-surface border border-swp-border rounded-swp p-6 mb-8" data-testid="submission-analytics">
      <div className="flex items-center gap-2 mb-5">
        <Bot size={18} className="text-swp-ice" />
        <h3 className="text-lg font-semibold text-white">Scrypto Analytics</h3>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MiniStat label="Total Pitches" value={data.total} sub={`+${data.total_7d} this week`} />
        <MiniStat label="Avg Score" value={data.scores.average} sub={`${data.scores.min}–${data.scores.max} range`} highlight />
        <MiniStat label="Assessed" value={data.assessed} sub={data.pending_assessment > 0 ? `${data.pending_assessment} pending` : 'All processed'} />
        <MiniStat label="Unreviewed" value={data.decisions.unreviewed} sub="Awaiting decision" alert={data.decisions.unreviewed > 0} />
      </div>

      {/* Decision Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Decisions Bar */}
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-3">Decisions Made</p>
          <div className="space-y-3">
            <DecisionBar label="Request Materials" count={data.decisions.request_materials} total={data.total} color="bg-blue-400" />
            <DecisionBar label="Pass" count={data.decisions.pass} total={data.total} color="bg-red-400/70" />
            <DecisionBar label="Unreviewed" count={data.decisions.unreviewed} total={data.total} color="bg-gray-500/50" />
          </div>
          {decisionTotal > 0 && (
            <p className="text-gray-500 text-xs mt-3 font-mono">
              {reqPct}% request rate across {decisionTotal} decided
            </p>
          )}
        </div>

        {/* Top Genres */}
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-3">Top Genres Submitted</p>
          <div className="space-y-2">
            {data.top_genres.map((g) => (
              <div key={g.genre} className="flex items-center justify-between">
                <span className="text-sm text-gray-300">{g.genre}</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-swp-ice/40 rounded-full" style={{ width: `${Math.min((g.count / data.total) * 100, 100)}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 font-mono w-6 text-right">{g.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, sub, highlight, alert }) => (
  <div className="bg-swp-deep/50 border border-swp-border/50 rounded-swp p-4">
    <p className="font-mono text-[8px] uppercase tracking-widest text-gray-500 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${alert ? 'text-yellow-400' : highlight ? 'text-swp-ice' : 'text-white'}`}>{value}</p>
    <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
  </div>
);

const DecisionBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.max((count / total) * 100, 1) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-400 w-32 truncate">{label}</span>
      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 font-mono w-8 text-right">{count}</span>
    </div>
  );
};

export default SubmissionAnalytics;
